'use client'

import * as Phosphor from '@phosphor-icons/react'
// Phosphor 2.x deprecated the bare canonical names (`X`) in favour of
// `*Icon` aliases (`XIcon`). The components are identical — just the
// import name changed — so we alias here to silence the deprecation
// warning. Stored values in multipass keep their bare canonical form
// (`House`, `Factory`, etc.); the registry below maps those to the
// underlying components.
import { type Icon, type IconProps, XIcon } from '@phosphor-icons/react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as React from 'react'

import { cn } from '../cn'
import { isBodyScrollLocked } from './popover'

/**
 * Full phosphor icon catalog (~1500 icons). The registry is built from
 * the package's namespace import so any phosphor icon — `Factory`,
 * `SquaresFour`, the long tail — works in the picker and the renderer
 * without a maintenance step on every new app.
 *
 * Bundle cost: every consumer of this module pulls in the entire
 * phosphor library (~1MB minified, ~250KB gzipped). That's acceptable
 * because the picker is admin-only and the renderer is the same module
 * — keeping picker + renderer in lockstep means an admin can pick any
 * icon without worrying that consuming apps render a fallback. If the
 * cost ever becomes a problem, split the renderer into a per-icon
 * dynamic import; until then, lockstep beats per-name maintenance.
 *
 * Filters: skip phosphor's non-icon utility exports (`IconBase`,
 * `IconContext`, the `SSR` namespace) and the duplicated `*Icon`
 * aliases that phosphor emits alongside each canonical name. What's
 * left is the canonical icon set.
 */
const NON_ICON_EXPORTS = new Set(['IconBase', 'IconContext', 'SSR'])

const ICON_REGISTRY: Record<string, Icon> = (() => {
  const out: Record<string, Icon> = {}
  for (const [name, value] of Object.entries(Phosphor)) {
    if (NON_ICON_EXPORTS.has(name)) {
      continue
    }
    if (name.endsWith('Icon')) {
      // Phosphor exports each icon twice — the canonical name (`Factory`)
      // and an alias (`FactoryIcon`). Drop the alias so the picker grid
      // doesn't show every icon twice.
      continue
    }
    out[name] = value as Icon
  }
  return out
})()

export const PHOSPHOR_ICON_NAMES: string[] = Object.keys(ICON_REGISTRY).sort()

export interface PhosphorIconProps extends Omit<IconProps, 'ref' | 'name'> {
  /** Phosphor component name (PascalCase) — see `PHOSPHOR_ICON_NAMES`. */
  name: string | null | undefined
  /** Component to render when `name` is null or unknown. Defaults to nothing. */
  fallback?: React.ReactNode
}

/**
 * Renders a phosphor icon by its component name (the value `IconPicker`
 * stores). Unknown names render the optional `fallback` so a deleted
 * icon never crashes the page.
 */
export function PhosphorIcon({ name, fallback = null, weight = 'bold', ...iconProps }: PhosphorIconProps) {
  if (!name) {
    return <>{fallback}</>
  }
  const Icon = ICON_REGISTRY[name]
  if (!Icon) {
    return <>{fallback}</>
  }
  return <Icon weight={weight} {...iconProps} />
}

export interface IconPickerProps {
  value: string | null
  onChange: (next: string | null) => void
  /** Override the trigger button text/icon. Defaults to a small preview tile. */
  trigger?: React.ReactNode
  /** Phosphor weight to render in the grid. Defaults to "bold" — matches
   *  the canonical app-icon weight stored in multipass. */
  weight?: IconProps['weight']
  /** Disable the trigger entirely (read-only forms). */
  disabled?: boolean
  className?: string
}

/**
 * Popover-backed icon picker. Consumers store the selected component
 * name as a string; render it back via `<PhosphorIcon name={value} />`.
 * The grid is filterable by case-insensitive substring match against
 * the component name.
 */
export function IconPicker({ value, onChange, trigger, weight = 'bold', disabled, className }: IconPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) {
      return PHOSPHOR_ICON_NAMES
    }
    return PHOSPHOR_ICON_NAMES.filter((name) => name.toLowerCase().includes(needle))
  }, [query])

  const SelectedIcon = value ? ICON_REGISTRY[value] : null

  const defaultTrigger = (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background',
        'text-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      aria-label={value ? `Selected icon: ${value}` : 'Pick an icon'}>
      {SelectedIcon ? (
        <SelectedIcon weight={weight} className="h-5 w-5" />
      ) : (
        <span className="text-xs text-muted-foreground">+</span>
      )}
    </button>
  )

  return (
    <PopoverPrimitive.Root modal={open && isBodyScrollLocked()} open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <span className={cn('inline-flex', className)}>{trigger ?? defaultTrigger}</span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'z-50 w-[20rem] max-w-[calc(100vw-16px)] rounded-md border bg-popover p-3 text-popover-foreground shadow-md outline-none',
            'max-h-[var(--radix-popover-content-available-height)] overflow-hidden flex flex-col gap-2',
          )}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search icons…"
            className={cn(
              'w-full rounded-md border border-input bg-background px-2 py-1 text-sm outline-none',
              'focus-visible:ring-2 focus-visible:ring-ring',
            )}
            autoFocus
          />
          {/* ~7 rows: the grid scrolls and the search narrows it, so a
              viewport-tall list only adds distance to the Clear button. */}
          <div className="grid max-h-64 grid-cols-8 gap-1 overflow-y-auto pr-1">
            {filtered.map((name) => {
              const Icon = ICON_REGISTRY[name]!
              const selected = name === value
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    onChange(name)
                    setOpen(false)
                  }}
                  aria-label={name}
                  aria-pressed={selected}
                  title={name}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    selected ? 'bg-accent text-accent-foreground ring-2 ring-ring' : 'text-foreground',
                  )}>
                  <Icon weight={weight} className="h-4 w-4" />
                </button>
              )
            })}
            {filtered.length === 0 ? (
              <div className="col-span-8 py-6 text-center text-sm text-muted-foreground">No icons match "{query}"</div>
            ) : null}
          </div>
          {value ? (
            <button
              type="button"
              onClick={() => {
                onChange(null)
                setOpen(false)
              }}
              className="self-start rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground inline-flex items-center gap-1">
              <XIcon className="h-3 w-3" weight="bold" />
              Clear selection
            </button>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
